import React, { useState, useEffect } from "react";
import {
  Input,
  Button,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
  Modal as ConfirmModal,
} from "reactstrap";

import { Btn } from "../../../../../AbstractElements";
import { DefaultButton } from "../../../../../AbstractElements";
import { Badges, H3 } from "../../../../../AbstractElements";
import Divider from "../../../../../CommonElements/Divider";
import FileHandler from "../../../../../CommonElements/FileHandler";
import FixedFooter from "../../../../../CommonElements/FixedFooter";
import LabelTooltip from "../../../../../CommonElements/LabelTooltip";
import MUIIcons from "../../../../../CommonElements/MUIIcon/MUIIcons";
import MultiFileUpload from "../../../../../CommonElements/MultiFileUpload/MultiFileUpload";
import ScrollableJustifyTabs from "../../../../../CommonElements/ScrollableJustifyTabs";
import "./style.scss";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../../../ReduxToolkit/Hooks";
import {
  updateSurvey,
  getSurveyDetail,
  createOrUpdateSurveyQuestion,
  deleteSurvey,
} from "../../../../../ReduxToolkit/Reducers/SurveySlice";
import { uploadFileApi } from "../../../../../ReduxToolkit/Reducers/UserSlice";
import { Back, Save, SaveAsDraft } from "../../../../../utils/Constant";
import {
  SurveyOverview,
  SectionsLabel,
  QuestionsLabel,
  RequiredLabel,
  WithBranchingLabel,
  BranchedQuestionsLabel,
  AddSectionLabel,
  SectionNamePlaceholder,
  AddSectionButton,
  TargetSectionLabel,
  QuestionTypeLabel,
  AddQuestionButton,
  UntitledSectionLabel,
  SectionTitlePlaceholder,
  SectionDescriptionPlaceholder,
  QuestionTitlePlaceholder,
  QuestionDescriptionPlaceholder,
  TypeLabel,
  ReferenceFileLabel,
  OptionsLabel,
  AddOptionButton,
  BranchingLogicButton,
  CreateQuestionButton,
  UpdateQuestionButton,
  BackToTopButton,
  SurveyPreviewTitle,
  SelectOptionLabel,
  EditBranchingLogicTitle,
  ConfigureBranchingRulesText,
  OptionLabel,
  TargetQuestionLabel,
  CancelButton,
  SaveBranchingRulesButton,
  BackToBuilderButton,
  PreviewSurveyButton,
} from "../../../../../utils/Constant";
import {
  fileUrlKeys,
  extractFileName,
} from "../../../../../utils/helper/helper";

interface BranchingRule {
  id: string;
  condition: string;
  value: string | number;
  targetQuestionId?: string;
  targetSectionId?: string;
  action: string;
  option_id?: number;
}

interface Question {
  id?: string;
  type: string;
  title: string;
  description?: string;
  required: boolean;
  options?: string[];
  sectionId: string;
  branchingRules?: BranchingRule[];
  file?: File | null;
  media_url?: string;
  isCreated?: boolean;
}

interface Section {
  id?: string;
  title: string;
  description?: string;
  orderIndex: number;
}

interface SurveyBuilderProps {
  onBack?: (payload: any) => void;
  onSubmit?: (data: any) => void;
  onSaveDraft?: (data: any) => void;
  surveySections?: any[];
  surveyQuestions?: any[];
}

const questionTypes = [
  { value: "open_text", label: "Open Text" },
  { value: "single_select", label: "Single Select" },
  { value: "multi_select", label: "Multi-Select" },
  { value: "rating", label: "Rating" },
  { value: "likert_scale", label: "Likert Scale" },
  { value: "ranking", label: "Ranking" },
  { value: "dropdown", label: "Dropdown" },
  { value: "date_picker", label: "Date Picker" },
];

const SurveyBuilder: React.FC<SurveyBuilderProps> = ({
  onBack,
  onSubmit,
  onSaveDraft,
  surveySections,
  surveyQuestions,
}) => {
  const [sections, setSections] = useState<any[]>(
    surveySections && surveySections.length > 0
      ? surveySections
      : [{ id: "default", title: "General Questions", orderIndex: 0 }]
  );
  const [questions, setQuestions] = useState<any[]>(
    surveyQuestions && surveyQuestions.length > 0 ? surveyQuestions : []
  );
  const [selectedSectionId, setSelectedSectionId] = useState("default");
  const [selectedQuestionType, setSelectedQuestionType] =
    useState<string>("open_text");
  const [branchingModal, setBranchingModal] = useState<{
    open: boolean;
    questionId: string | null;
  }>({ open: false, questionId: null });
  const [branchingDraft, setBranchingDraft] = useState<BranchingRule[]>([]);
  const [previewMode, setPreviewMode] = useState(false);
  const errors: Record<string, string> = {};
  const [newSectionName, setNewSectionName] = useState("");
  const dispatch = useAppDispatch();
  const { surveyDetails } = useAppSelector((state: any) => state.survey);
  const surveyId = surveyDetails?.id;

  const [questionLoading, setQuestionLoading] = useState(false);
  const [questionError, setQuestionError] = useState<string>("");

  // Modal state for delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetType, setDeleteTargetType] = useState<
    "section" | "question" | null
  >(null);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [pendingSectionId, setPendingSectionId] = useState<string | null>(null); // for section selection after update

  useEffect(() => {
    if (surveySections && surveySections.length > 0) {
      setSections(surveySections);
      setSelectedSectionId(surveySections[0].id);
    }
  }, [surveySections]);

  useEffect(() => {
    if (
      surveyDetails &&
      surveyDetails.sections &&
      surveyDetails.sections.length > 0
    ) {
      setSections(surveyDetails.sections);
      const sectionIds = surveyDetails.sections.map(s => s.id);
      // If current selectedSectionId is still present, keep it. Otherwise, select the first.
      if (sectionIds.includes(selectedSectionId)) {
        setSelectedSectionId(selectedSectionId);
      } else {
        setSelectedSectionId(surveyDetails.sections[0].id);
      }
      const branchingRules = surveyDetails.branching_rules || [];
      const allQuestions = surveyDetails.sections.flatMap(section =>
        (section.questions || []).map(q => {
          const qBranchingRules = branchingRules
            ? branchingRules
                .filter(
                  rule => String(rule.parent_question_id) === String(q.id)
                )
                .map(rule => ({
                  id: rule.id
                    ? String(rule.id)
                    : Date.now().toString() + Math.random(),
                  condition: rule.condition_type || "selected",
                  value: rule.condition_value,
                  targetQuestionId: rule.child_question_id
                    ? String(rule.child_question_id)
                    : undefined,
                  targetSectionId: rule.child_section_id
                    ? String(rule.child_section_id)
                    : undefined,
                  action: rule.action || "show_question",
                  option_id: rule.option_id,
                }))
            : [];
          return {
            ...q,
            id: q.id ? String(q.id) : undefined,
            sectionId: section.id ? String(section.id) : undefined,
            isCreated: true,
            branchingRules: qBranchingRules,
            type: q.question_type || q.type,
            required: q.is_required ?? false,
            options: Array.isArray(q.options)
              ? q.options.map(opt =>
                  typeof opt === "string" ? opt : opt.option_text
                )
              : [],
          };
        })
      );
      setQuestions(allQuestions);
    }
  }, [surveyDetails]);

  const addSection = async () => {
    if (!newSectionName.trim()) return;
    const newSection: Section = {
      title: newSectionName,
      orderIndex: sections.length,
    };
    const updatedSections = [...sections, newSection];
    setSections(updatedSections);
    setNewSectionName("");

    if (surveyId) {
      const payload = buildCurrentPayload(
        surveyDetails?.status || "draft",
        updatedSections
      );
      await dispatch(updateSurvey(payload));
      await dispatch(getSurveyDetail(surveyId));
    }
  };

  const updateSection = (id: string, updates: Partial<Section>) => {
    setSections(
      sections.map(s => (s.id === id ? { ...s, ...(updates || {}) } : s))
    );
  };

  const addQuestion = () => {
    const newQuestion: Question = {
      id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: selectedQuestionType,
      title: "New Question",
      required: false,
      sectionId: selectedSectionId,
      options: [
        "single_select",
        "multi_select",
        "dropdown",
        "ranking",
        "likert_scale",
      ].includes(selectedQuestionType)
        ? ["Option 1", "Option 2"]
        : undefined,
      branchingRules: [],
      file: null,
      isCreated: false,
    };
    setQuestions([...questions, newQuestion]);
    setSelectedSectionId(newQuestion.sectionId);
  };

  const updateQuestion = (id: string, updates: Partial<Question>) => {
    setQuestions(
      questions.map(q => (q.id === id ? { ...q, ...(updates || {}) } : q))
    );
  };

  // --- Delete Handlers ---
  const handleDeleteClick = (type: "section" | "question", id: string) => {
    setDeleteTargetType(type);
    setDeleteTargetId(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTargetType || !deleteTargetId) return;
    const payload = await buildCurrentPayload("draft");
    await dispatch(updateSurvey(payload));
    if (deleteTargetType === "section") {
      // Call deleteSurvey for section
      await dispatch(deleteSurvey({ id: deleteTargetId, type: "section" }));
      if (surveyId) {
        await dispatch(getSurveyDetail(surveyId));
      }
    } else if (deleteTargetType === "question") {
      // Call deleteSurvey for question
      await dispatch(deleteSurvey({ id: deleteTargetId, type: "question" }));
      if (surveyId) {
        await dispatch(getSurveyDetail(surveyId));
      }
    }
    setDeleteModalOpen(false);
    setDeleteTargetType(null);
    setDeleteTargetId(null);
  };

  // --- Section selection after update ---
  useEffect(() => {
    if (pendingSectionId) {
      setSelectedSectionId(pendingSectionId);
      setPendingSectionId(null);
    }
  }, [sections, pendingSectionId]);

  // --- Fix: After saving question, always select its section ---
  const handleSaveQuestion = async (question: Question) => {
    setQuestionError("");

    if (!question.title.trim()) {
      setQuestionError("Question title is required.");
      return;
    }

    if (["single_select", "multi_select", "dropdown"].includes(question.type)) {
      const opts = question?.options || [];
      const nonEmptyOpts = opts.filter(opt => opt.trim());
      if (nonEmptyOpts.length < 2) {
        setQuestionError(
          "At least 2 non-empty options are required for single select, multi select, or dropdown questions."
        );
        return;
      }
    }

    if (!surveyId || !question.sectionId) return;

    setQuestionLoading(true);
    let media_url = question.media_url || "";

    if (question.file && !media_url) {
      const postData = new FormData();
      postData.append("files", question.file);
      const response = await dispatch(
        uploadFileApi({ content: postData, key: fileUrlKeys.survey_files })
      );
      const urls = response?.payload?.data?.file_urls;
      if (Array.isArray(urls) && urls.length > 0) {
        media_url = urls[0];
        updateQuestion(question.id, { media_url, file: null });
      }
    }

    // Format media_url as per requirement
    let formattedMediaUrl = "";
    if (media_url && typeof media_url === "string") {
      formattedMediaUrl =
        fileUrlKeys.survey_files + "/" + extractFileName(media_url);
    }

    const questionPayload = {
      survey_id: surveyId,
      section_id: Number(question.sectionId),
      question: {
        ...(question.id && !String(question.id).startsWith("temp_")
          ? { id: question.id }
          : {}),
        question_type: question.type,
        title: question.title,
        description: question.description || "",
        is_required: question.required,
        question_order:
          questions
            .filter(q => q.sectionId === question.sectionId)
            .indexOf(question) + 1,
        is_branch_question: !!(
          question.branchingRules && question.branchingRules.length > 0
        ),
        media_url: formattedMediaUrl,
        options: question.options
          ? question.options.map((opt: string, idx: number) => ({
              option_text: opt,
              option_order: idx + 1,
            }))
          : undefined,
      },
    };

    try {
      const payload = await buildCurrentPayload("draft");
      await dispatch(updateSurvey(payload));
      const result = await dispatch(
        createOrUpdateSurveyQuestion(questionPayload)
      );
      setQuestionLoading(false);

      if (result.meta.requestStatus === "fulfilled") {
        // Refetch survey details to get the latest questions and sections
        if (surveyId) {
          await dispatch(getSurveyDetail(surveyId));
        }
        // No need to update local questions here, as useEffect will update them from surveyDetails
      }
    } catch (error) {
      setQuestionLoading(false);
      setQuestionError("Failed to save question. Please try again.");
    }
  };

  const stats = {
    sections: sections.length,
    questions: questions.length,
    required: questions.filter(q => q.required).length,
    withBranching: questions.filter(
      q => q.branchingRules && q.branchingRules.length > 0
    ).length,
    branchedQuestions: questions.reduce(
      (acc, q) => acc + (q.branchingRules ? q.branchingRules.length : 0),
      0
    ),
    estimatedTime: Math.max(1, Math.round(questions.length * 0.5)),
  };

  const getCreatedQuestions = () => {
    return questions.filter(q => q.isCreated && q.id);
  };

  const openBranchingModal = (questionId: string) => {
    setBranchingModal({ open: true, questionId });
    const question = questions.find(q => q.id === questionId);
    setBranchingDraft(
      question?.branchingRules ? [...question.branchingRules] : []
    );
  };

  const buildCurrentPayload = (
    status = surveyDetails?.status || "draft",
    newSection = sections,
    newQuestions = questions
  ) => {
    const realQuestions = (newQuestions || questions).filter(
      q => q.id && !String(q.id).startsWith("temp_")
    );
    const payload: any = {
      id: surveyId,
      title: surveyDetails?.title || "",
      description: surveyDetails?.description || "",
      status: status,
      sections: newSection.map((section, idx) => ({
        id: section.id,
        title: section.title,
        description: section.description,
        section_order: idx + 1,
        questions: realQuestions
          .filter(
            (q: any) => q.sectionId?.toString() === section.id?.toString()
          )
          .map((q: any, qIdx: number) => ({
            id: q.id,
            question_type: q.type,
            title: q.title,
            description: q.description,
            is_required: q.required || false,
            question_order: qIdx + 1,
            is_branch_question: !!(
              q.branchingRules && q.branchingRules.length > 0
            ),
            media_url:
              q.media_url && typeof q.media_url === "string"
                ? fileUrlKeys.survey_files + "/" + extractFileName(q.media_url)
                : "",
            options: q.options
              ? q.options.map((opt: string, oIdx: number) => ({
                  option_text: opt,
                  option_order: oIdx + 1,
                }))
              : undefined,
          })),
      })),
      branching_rules: realQuestions.flatMap(q =>
        (q.branchingRules || []).map(rule => ({
          ...(rule.id ? { id: rule.id } : {}), // Always include id if present
          parent_question_id: q.id,
          child_question_id:
            !rule.targetQuestionId ||
            rule.targetQuestionId === undefined ||
            rule.targetQuestionId === ""
              ? null
              : rule.targetQuestionId,
          condition_type: rule.condition,
          condition_value: rule.value,
          option_id: rule.option_id,
        }))
      ),
    };
    // Add survey-specific keys if present and valid
    if (
      surveyDetails?.duration &&
      surveyDetails?.duration != null &&
      surveyDetails?.duration !== ""
    ) {
      payload.duration = surveyDetails.duration;
    }
    if (
      surveyDetails?.onboarding_survey_offset &&
      surveyDetails?.onboarding_survey_offset != null &&
      surveyDetails?.onboarding_survey_offset !== ""
    ) {
      payload.onboarding_survey_offset = surveyDetails.onboarding_survey_offset;
    }
    if (
      surveyDetails?.is_multiple_submissions &&
      surveyDetails?.is_multiple_submissions != null &&
      surveyDetails?.is_multiple_submissions !== ""
    ) {
      payload.is_multiple_submissions = surveyDetails.is_multiple_submissions;
    }
    if (
      surveyDetails?.resubmission_limit &&
      surveyDetails?.resubmission_limit != null &&
      surveyDetails?.resubmission_limit !== ""
    ) {
      payload.resubmission_limit = surveyDetails.resubmission_limit;
    }
    if (
      surveyDetails?.course_id &&
      surveyDetails?.course_id != null &&
      surveyDetails?.course_id !== ""
    ) {
      payload.course_id = surveyDetails.course_id;
    }
    return payload;
  };

  return (
    <div className="survey-builder__main-panel d-flex">
      <div className="survey-builder__sidebar survey-builder__sidebar--fixed">
        <div className="mb-2">
          <h5 className="survey-builder__overview-title">{SurveyOverview}</h5>
          <div className="survey-builder__overview-stats">
            <div className="survey-builder__overview-stat-row">
              <span>{SectionsLabel}:</span>
              <span className="survey-builder__overview-stat-value">
                {stats.sections}
              </span>
            </div>
            <div className="survey-builder__overview-stat-row">
              <span>{QuestionsLabel}:</span>
              <span className="survey-builder__overview-stat-value">
                {stats.questions}
              </span>
            </div>
            <div className="survey-builder__overview-stat-row">
              <span>{RequiredLabel}:</span>
              <span className="survey-builder__overview-stat-value">
                {stats.required}
              </span>
            </div>
            <div className="survey-builder__overview-stat-row">
              <span>{WithBranchingLabel}:</span>
              <span className="survey-builder__overview-stat-value">
                {stats.withBranching}
              </span>
            </div>
            <div className="survey-builder__overview-stat-row">
              <span>{BranchedQuestionsLabel}:</span>
              <span className="survey-builder__overview-stat-value">
                {stats.branchedQuestions}
              </span>
            </div>
          </div>
        </div>
        <Divider />
        <div className="mb-16">
          <Label htmlFor="new-section-name">{AddSectionLabel}</Label>
          <Input
            id="new-section-name"
            type="text"
            placeholder={SectionNamePlaceholder}
            value={newSectionName}
            onChange={e => setNewSectionName(e.target.value)}
            className="mb-2"
          />
          <Button
            color="primary"
            block
            onClick={addSection}
            disabled={!newSectionName.trim()}
          >
            {AddSectionButton}
          </Button>
        </div>
        <Divider />
        <div className="mb-16">
          <Label>{TargetSectionLabel}</Label>
          <Input
            type="select"
            value={selectedSectionId}
            onChange={e => setSelectedSectionId(e.target.value)}
          >
            {sections.map(section => (
              <option key={section.id} value={section.id}>
                {section.title}
              </option>
            ))}
          </Input>
        </div>
        <div className="mb-16">
          <Label>{QuestionTypeLabel}</Label>
          <Input
            type="select"
            value={selectedQuestionType}
            onChange={e => setSelectedQuestionType(e.target.value)}
          >
            {questionTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </Input>
        </div>
        <Button color="primary" block onClick={addQuestion}>
          {AddQuestionButton}
        </Button>
      </div>
      <div className="survey-builder__content">
        <div className="survey-builder__tabs mb-3">
          <ScrollableJustifyTabs
            tabs={sections.map(section => ({
              id: section.id,
              label: section.title || UntitledSectionLabel,
            }))}
            activeTab={selectedSectionId}
            onTabClick={setSelectedSectionId}
          />
        </div>
        {!previewMode ? (
          sections
            .filter(section => section.id === selectedSectionId)
            .map(section => (
              <div key={section.id}>
                <Card>
                  <CardBody>
                    <div className="survey-builder__section-header">
                      <div className="mb-2">
                        <span
                          className={
                            sections.length <= 1
                              ? "float-end survey-builder__section-delete disabled"
                              : "float-end survey-builder__section-delete"
                          }
                        >
                          <MUIIcons
                            iconName="DeleteOutlineOutlined"
                            className="primary-icon-color pointer"
                            size={24}
                            onClick={() =>
                              handleDeleteClick("section", section.id)
                            }
                          />
                        </span>
                        <LabelTooltip
                          label="Section Name"
                          tooltipText="Enter the section name"
                          important
                          forHtml={`section-title-${section.id}`}
                        />
                        <Input
                          id={`section-title-${section.id}`}
                          className="survey-builder__section-title"
                          value={section.title}
                          onChange={e =>
                            updateSection(section.id, {
                              title: e.target.value,
                            })
                          }
                          placeholder={SectionTitlePlaceholder}
                        />
                      </div>
                    </div>
                    <div className="mb-2">
                      <LabelTooltip
                        label="Section Description"
                        tooltipText="Enter the section description"
                        important={false}
                        forHtml={`section-desc-${section.id}`}
                      />
                      <Input
                        id={`section-desc-${section.id}`}
                        value={section.description || ""}
                        onChange={e =>
                          updateSection(section.id, {
                            description: e.target.value,
                          })
                        }
                        placeholder={SectionDescriptionPlaceholder}
                        className="mb-2"
                      />
                    </div>
                  </CardBody>
                </Card>
                <div className="survey-builder__questions-scroll">
                  {questions
                    .filter(
                      q => q.sectionId?.toString() === section.id?.toString()
                    )
                    .map(q => (
                      <Card key={q.id} className="mb-3">
                        <CardBody>
                          <div className="mb-2">
                            <LabelTooltip
                              label="Question Name"
                              tooltipText="Enter the question name"
                              important
                              forHtml={`question-title-${q.id}`}
                            />
                            <div className="d-flex align-items-center gap-2">
                              <Input
                                id={`question-title-${q.id}`}
                                value={q.title}
                                onChange={e =>
                                  updateQuestion(q.id, {
                                    title: e.target.value,
                                  })
                                }
                                placeholder={QuestionTitlePlaceholder}
                                invalid={!!errors[`question_${q.id}_title`]}
                              />
                              <MUIIcons
                                iconName="DeleteOutlineOutlined"
                                className="primary-icon-color pointer"
                                size={24}
                                onClick={() =>
                                  handleDeleteClick("question", q.id)
                                }
                              />
                            </div>
                          </div>
                          <div className="mb-2">
                            <LabelTooltip
                              label="Question Description"
                              tooltipText="Enter the question description"
                              important={false}
                              forHtml={`question-desc-${q.id}`}
                            />
                            <Input
                              id={`question-desc-${q.id}`}
                              value={q.description || ""}
                              onChange={e =>
                                updateQuestion(q.id, {
                                  description: e.target.value,
                                })
                              }
                              placeholder={QuestionDescriptionPlaceholder}
                              className="mb-2"
                            />
                          </div>
                          <FormGroup check className="mb-2">
                            <Label check>
                              <Input
                                type="checkbox"
                                className="primary-checkbox"
                                checked={q.required}
                                onChange={e =>
                                  updateQuestion(q.id, {
                                    required: e.target.checked,
                                  })
                                }
                              />
                              {RequiredLabel}
                            </Label>
                          </FormGroup>
                          <FormGroup>
                            <Label>{TypeLabel}</Label>
                            <Input
                              type="select"
                              value={q.type}
                              onChange={e =>
                                updateQuestion(q.id, {
                                  type: e.target.value,
                                })
                              }
                            >
                              {questionTypes.map(type => (
                                <option key={type.value} value={type.value}>
                                  {type.label}
                                </option>
                              ))}
                            </Input>
                          </FormGroup>
                          <div className="mb-3">
                            <Label>{ReferenceFileLabel}</Label>
                            {q.media_url && q.media_url !== "" ? (
                              <H3>
                                <Badges
                                  className="pointer"
                                  color="light text-dark"
                                >
                                  <div className="d-flex gap-2">
                                    <MUIIcons
                                      size={24}
                                      iconName="InsertDriveFileOutlined"
                                    />
                                    <FileHandler mediaUrl={q.media_url} />
                                    <MUIIcons
                                      iconName="DeleteOutlineOutlined"
                                      className="primary-icon-color pointer"
                                      size={24}
                                      onClick={() =>
                                        updateQuestion(q.id, { media_url: "" })
                                      }
                                    />
                                  </div>
                                </Badges>
                              </H3>
                            ) : (
                              <MultiFileUpload
                                accept=".pdf,.docx,.mp4,.mp3,.png,.jpg,.jpeg"
                                multiple={false}
                                onFileChange={async (file, status) => {
                                  if (status === "removed") {
                                    updateQuestion(q.id, {
                                      media_url: "",
                                      file: null,
                                    });
                                  } else if (status === "done" && file) {
                                    updateQuestion(q.id, { file });
                                  }
                                }}
                              />
                            )}
                          </div>

                          {[
                            "single_select",
                            "multi_select",
                            "dropdown",
                            "ranking",
                          ].includes(q.type) && (
                            <div className="survey-builder__question-options-row">
                              <Label>{OptionsLabel}</Label>
                              {q.options?.map((opt, optIdx) => (
                                <div
                                  key={optIdx}
                                  className="survey-builder__option-row"
                                >
                                  <Input
                                    className="survey-builder__option-input"
                                    value={opt}
                                    onChange={e => {
                                      const newOpts = [...(q.options || [])];
                                      newOpts[optIdx] = e.target.value;
                                      updateQuestion(q.id, {
                                        options: newOpts,
                                      });
                                    }}
                                  />
                                  <span
                                    className={
                                      q.options && q.options.length <= 2
                                        ? "survey-builder__option-delete disabled"
                                        : "survey-builder__option-delete"
                                    }
                                  >
                                    <MUIIcons
                                      iconName="DeleteOutlineOutlined"
                                      className="primary-icon-color pointer"
                                      size={20}
                                      onClick={() => {
                                        const newOpts = (
                                          q.options || []
                                        ).filter((_, i) => i !== optIdx);
                                        updateQuestion(q.id, {
                                          options: newOpts,
                                        });
                                      }}
                                    />
                                  </span>
                                </div>
                              ))}
                              <div className="survey-builder__option-actions-row">
                                <Button
                                  size="sm"
                                  color="primary"
                                  onClick={() =>
                                    updateQuestion(q.id, {
                                      options: [
                                        ...(q.options || []),
                                        `${AddOptionButton} ${(q.options ? q.options.length : 0) + 1}`,
                                      ],
                                    })
                                  }
                                >
                                  {AddOptionButton}
                                </Button>
                              </div>
                            </div>
                          )}

                          {questionError && (
                            <div className="text-danger mb-2">
                              {questionError}
                            </div>
                          )}
                          <div className="d-flex float-end gap-3">
                            {[
                              "single_select",
                              "multi_select",
                              "dropdown",
                              "ranking",
                              "rating",
                              "likert_scale",
                              "date_picker",
                            ].includes(q.type) &&
                              q.isCreated && (
                                <div className="mt-2">
                                  <Btn
                                    outline
                                    className="alert-light-primary"
                                    color="primary"
                                    size="sm"
                                    onClick={() => openBranchingModal(q.id)}
                                  >
                                    {BranchingLogicButton}
                                  </Btn>
                                </div>
                              )}
                            <Button
                              className="mt-2"
                              color="primary"
                              onClick={() => handleSaveQuestion(q)}
                              disabled={questionLoading}
                            >
                              {q.isCreated
                                ? UpdateQuestionButton
                                : CreateQuestionButton}
                            </Button>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                </div>
                {questions.filter(q => q.sectionId === section.id).length >
                  5 && (
                  <div className="survey-builder__back-to-top">
                    <button
                      className="survey-builder__back-to-top-btn"
                      onClick={() =>
                        window.scrollTo({ top: 0, behavior: "smooth" })
                      }
                    >
                      {BackToTopButton}
                    </button>
                  </div>
                )}
              </div>
            ))
        ) : (
          <div className="survey-builder__preview-panel">
            <h4 className="survey-builder__preview-title">
              {SurveyPreviewTitle}
            </h4>
            {sections.map(section => (
              <div className="survey-builder__preview-section" key={section.id}>
                <h5 className="survey-builder__preview-section-title">
                  {section.title}
                </h5>
                <div className="survey-builder__preview-section-desc">
                  {section.description}
                </div>
                {questions
                  .filter(q => q.sectionId === section.id)
                  .map(q => (
                    <div
                      className="survey-builder__preview-question"
                      key={q.id}
                    >
                      <div className="survey-builder__preview-question-title">
                        {q.title}{" "}
                        {q.required && (
                          <span className="survey-builder__preview-required">
                            *
                          </span>
                        )}
                      </div>
                      <div className="survey-builder__preview-question-desc">
                        {q.description}
                      </div>
                      {q.media_url && (
                        <div className="survey-builder__preview-media">
                          <FileHandler mediaUrl={q.media_url} />
                        </div>
                      )}
                      {q.type === "open_text" && (
                        <Input placeholder="Short answer" disabled />
                      )}
                      {q.type === "single_select" &&
                        q.options &&
                        q.options.map((opt, i) => (
                          <FormGroup key={i} check>
                            <Input type="radio" disabled /> {opt}
                          </FormGroup>
                        ))}
                      {q.type === "multi_select" &&
                        q.options &&
                        q.options.map((opt, i) => (
                          <FormGroup key={i} check>
                            <Input type="checkbox" disabled /> {opt}
                          </FormGroup>
                        ))}
                      {q.type === "dropdown" && (
                        <Input type="select" disabled>
                          <option>{SelectOptionLabel}</option>
                          {q.options &&
                            q.options.map((opt, i) => (
                              <option key={i}>{opt}</option>
                            ))}
                        </Input>
                      )}
                      {q.type === "rating" && (
                        <div className="survey-builder__preview-placeholder">
                          [Rating scale here]
                        </div>
                      )}
                      {q.type === "likert_scale" && (
                        <div className="survey-builder__preview-placeholder">
                          [Likert scale here]
                        </div>
                      )}
                      {q.type === "ranking" && (
                        <div className="survey-builder__preview-placeholder">
                          [Ranking input here]
                        </div>
                      )}
                      {q.type === "date_picker" && (
                        <Input type="date" disabled />
                      )}
                    </div>
                  ))}
              </div>
            ))}
          </div>
        )}
      </div>
      <Modal
        isOpen={branchingModal.open}
        toggle={() => setBranchingModal({ open: false, questionId: null })}
        size="lg"
      >
        <ModalHeader
          toggle={() => setBranchingModal({ open: false, questionId: null })}
        >
          {EditBranchingLogicTitle}
        </ModalHeader>
        <ModalBody>
          {branchingModal.questionId && (
            <div>
              <p className="mb-3">{ConfigureBranchingRulesText}</p>
              {(() => {
                const question = questions.find(
                  q => q.id === branchingModal.questionId
                );
                if (!question) return null;
                // For single/multi/dropdown/ranking: old logic (options)
                if (
                  [
                    "single_select",
                    "multi_select",
                    "dropdown",
                    "ranking",
                  ].includes(question.type) &&
                  question.options
                ) {
                  return question.options.map((option, optionIndex) => (
                    <div key={optionIndex} className="mb-4 p-3 border rounded">
                      <h6 className="mb-2">
                        {OptionLabel}: {option}
                      </h6>
                      <FormGroup>
                        <Label>{TargetQuestionLabel}</Label>
                        <Input
                          type="select"
                          value={
                            branchingDraft.find(
                              rule =>
                                rule.condition === "selected" &&
                                rule.value === option
                            )?.targetQuestionId || ""
                          }
                          onChange={e => {
                            const newDraft = [...branchingDraft];
                            const existingRuleIndex = newDraft.findIndex(
                              rule =>
                                rule.condition === "selected" &&
                                rule.value === option
                            );
                            if (existingRuleIndex >= 0) {
                              newDraft[existingRuleIndex].targetQuestionId =
                                e.target.value;
                            } else {
                              newDraft.push({
                                id: Date.now().toString(),
                                condition: "selected",
                                value: option,
                                targetQuestionId: e.target.value,
                                action: "show_question",
                                option_id: optionIndex + 1,
                              });
                            }
                            setBranchingDraft(newDraft);
                          }}
                        >
                          <option value="">{SelectOptionLabel}</option>
                          {getCreatedQuestions()
                            .filter(targetQ => targetQ.id !== question.id)
                            .map(targetQ => (
                              <option key={targetQ.id} value={targetQ.id}>
                                {targetQ.title}
                              </option>
                            ))}
                        </Input>
                      </FormGroup>
                    </div>
                  ));
                }
                // For rating, likert_scale, date_picker: new logic
                if (
                  ["rating", "likert_scale", "date_picker"].includes(
                    question.type
                  )
                ) {
                  // Helper for condition options
                  const conditionOptions = [
                    { value: "equals", label: "Equals" },
                    { value: "not_equals", label: "Not Equals" },
                    { value: "contains", label: "Contains" },
                    { value: "not_contains", label: "Not Contains" },
                    { value: "selected", label: "Selected" },
                    { value: "not_selected", label: "Not Selected" },
                    { value: "greater_than", label: "Greater Than" },
                    { value: "less_than", label: "Less Than" },
                    { value: "is_empty", label: "Is Empty" },
                    { value: "is_not_empty", label: "Is Not Empty" },
                  ];
                  const maxValue = 6;
                  return (
                    <>
                      {branchingDraft.map((rule, idx) => (
                        <div
                          key={rule.id}
                          className="mb-4 p-3 border rounded bg-light"
                        >
                          <div className="row g-3 align-items-end">
                            <div className="col-md-4">
                              <FormGroup>
                                <Label className="fw-bold text-dark">
                                  Condition
                                </Label>
                                <Input
                                  type="select"
                                  value={rule.condition || ""}
                                  onChange={e => {
                                    const newDraft = [...branchingDraft];
                                    newDraft[idx].condition = e.target.value;
                                    setBranchingDraft(newDraft);
                                  }}
                                >
                                  <option value="">Select</option>
                                  {conditionOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Input>
                              </FormGroup>
                            </div>
                            <div className="col-md-4">
                              <FormGroup>
                                <Label className="fw-bold text-dark">
                                  Value
                                </Label>
                                {question.type === "date_picker" ? (
                                  <Input
                                    type="date"
                                    value={rule.value || ""}
                                    onChange={e => {
                                      const newDraft = [...branchingDraft];
                                      newDraft[idx].value = e.target.value;
                                      setBranchingDraft(newDraft);
                                    }}
                                  />
                                ) : (
                                  <Input
                                    type="number"
                                    min={1}
                                    max={maxValue}
                                    value={rule.value || ""}
                                    onChange={e => {
                                      const val = e.target.value;
                                      // Only allow numbers between 1 and maxValue
                                      if (val !== "") {
                                        const numVal = Number(val);
                                        if (
                                          isNaN(numVal) ||
                                          numVal < 1 ||
                                          numVal > maxValue
                                        )
                                          return;
                                      }
                                      const newDraft = [...branchingDraft];
                                      newDraft[idx].value = val;
                                      setBranchingDraft(newDraft);
                                    }}
                                  />
                                )}
                              </FormGroup>
                            </div>
                            <div className="col-md-4">
                              <FormGroup>
                                <Label className="fw-bold text-dark">
                                  Target Question
                                </Label>
                                <Input
                                  type="select"
                                  value={rule.targetQuestionId || ""}
                                  onChange={e => {
                                    const newDraft = [...branchingDraft];
                                    newDraft[idx].targetQuestionId =
                                      e.target.value;
                                    setBranchingDraft(newDraft);
                                  }}
                                >
                                  <option value="">Select</option>
                                  {getCreatedQuestions()
                                    .filter(q2 => q2.id !== question.id)
                                    .map(q2 => (
                                      <option key={q2.id} value={q2.id}>
                                        {q2.title}
                                      </option>
                                    ))}
                                </Input>
                              </FormGroup>
                            </div>
                            <div className="col-md-12 text-end">
                              <Button
                                size="sm"
                                color="danger"
                                outline
                                className="align-self-end mt-2"
                                onClick={() => {
                                  const newDraft = branchingDraft.filter(
                                    (_, i) => i !== idx
                                  );
                                  setBranchingDraft(newDraft);
                                }}
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      <div className="text-center mt-3">
                        <Button
                          size="sm"
                          color="primary"
                          outline
                          onClick={() =>
                            setBranchingDraft([
                              ...branchingDraft,
                              {
                                id: Date.now().toString(),
                                condition: "",
                                value: "",
                                targetQuestionId: "",
                                action: "show_question",
                              },
                            ])
                          }
                        >
                          Add Rule
                        </Button>
                      </div>
                    </>
                  );
                }
                return null;
              })()}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={() => setBranchingModal({ open: false, questionId: null })}
          >
            {CancelButton}
          </Button>
          <Button
            color="primary"
            onClick={() => {
              if (branchingModal.questionId) {
                const qIdx = questions.findIndex(
                  q => q.id === branchingModal.questionId
                );
                if (qIdx !== -1) {
                  const updatedQuestions = [...questions];
                  updatedQuestions[qIdx] = {
                    ...updatedQuestions[qIdx],
                    branchingRules: branchingDraft,
                  };
                  setQuestions(updatedQuestions);
                  setTimeout(() => {
                    setBranchingModal({ open: false, questionId: null });
                    setBranchingDraft([]);
                  }, 0);
                }
              }
            }}
          >
            {SaveBranchingRulesButton}
          </Button>
        </ModalFooter>
      </Modal>

      <FixedFooter>
        <Btn
          outline
          className="alert-light-primary"
          color="primary"
          onClick={() => setPreviewMode(m => !m)}
        >
          {previewMode ? BackToBuilderButton : PreviewSurveyButton}
        </Btn>
        <Button
          color="primary"
          outline
          disabled={branchingModal.open || questionLoading}
          onClick={async () => {
            const payload = await buildCurrentPayload("draft");
            if (onBack) onBack(payload);
          }}
        >
          {Back}
        </Button>

        <Button
          color="primary"
          outline
          disabled={branchingModal.open || questionLoading}
          onClick={() => {
            if (onSaveDraft) onSaveDraft(buildCurrentPayload("draft"));
          }}
        >
          {SaveAsDraft}
        </Button>
        <DefaultButton
          color="primary"
          disabled={branchingModal.open || questionLoading}
          onClick={() => {
            if (onSubmit) onSubmit(buildCurrentPayload("pending_approval"));
          }}
        >
          {Save}
        </DefaultButton>
      </FixedFooter>

      <ConfirmModal
        isOpen={deleteModalOpen}
        toggle={() => setDeleteModalOpen(false)}
        backdrop="static"
      >
        <ModalHeader toggle={() => setDeleteModalOpen(false)}>
          {deleteTargetType === "section"
            ? "Delete Section"
            : "Delete Question"}
        </ModalHeader>
        <ModalBody>
          {deleteTargetType === "section"
            ? "Are you sure you want to delete this section?"
            : "Are you sure you want to delete this question?"}
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            outline
            onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </ModalFooter>
      </ConfirmModal>
    </div>
  );
};

export default SurveyBuilder;
